import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Presentation, ExternalLink } from "lucide-react";
import { DEMO_BANNER_TEXT, DEMO_CTA_FALLBACK_MESSAGE } from "@/lib/demoMode";
import { toast } from "sonner";

/**
 * Persistent banner shown on every page for Presentation Mode (demo)
 * users. Fetches the admin-configured "View Business Systems" destination
 * (defaults to the 815 Digital Services website) from Settings; if none
 * has been configured yet, the CTA button shows a friendly "coming soon,
 * contact us" message instead of a dead link.
 */
export const PresentationModeBanner = () => {
  const [ctaUrl, setCtaUrl] = useState(null);

  useEffect(() => {
    api
      .get("/settings/demo-cta")
      .then((res) => setCtaUrl(res.data?.url || null))
      .catch(() => setCtaUrl(null));
  }, []);

  const handleCtaClick = () => {
    if (ctaUrl) {
      window.open(ctaUrl, "_blank", "noopener,noreferrer");
    } else {
      toast.info(DEMO_CTA_FALLBACK_MESSAGE, { duration: 6000 });
    }
  };

  return (
    <div
      className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4 px-4 sm:px-6 py-2.5 bg-[hsl(var(--ink))] text-white"
      data-testid="presentation-mode-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Presentation className="h-4 w-4 shrink-0 text-[hsl(var(--gold-bright))]" />
        <p className="text-xs sm:text-sm font-medium leading-snug" data-testid="presentation-mode-banner-text">
          {DEMO_BANNER_TEXT}
        </p>
      </div>
      <Button
        size="sm"
        className="h-8 shrink-0 bg-[hsl(var(--gold))] text-[hsl(var(--ink))] hover:bg-[hsl(var(--gold-bright))] font-semibold"
        onClick={handleCtaClick}
        data-testid="presentation-mode-cta-button"
      >
        View Business Systems <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
      </Button>
    </div>
  );
};
